import React from 'react';
import { formatDate } from '../lib/api';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * PACIENTE PROHIBIDO (administración, 14/10/2026)
 * -----------------------------------------------
 * El indicador ROJO «PROHIBIDO» junto al nombre del paciente: en su ficha y en
 * cualquier listado o búsqueda donde aparezca (Pacientes, el selector de
 * paciente, el buscador ⌘K, el resumen de la cita y la agenda).
 *
 * Al pasar el cursor (el `title`) o al pulsarlo (`interactivo`), el MOTIVO,
 * que es de uso interno: lo ve el personal que ve la ficha. Donde el
 * indicador va DENTRO de algo que ya se pulsa —una fila del selector, una
 * cita de la agenda— se pinta sin botón (`interactivo={false}`): un botón
 * dentro de otro no se puede pulsar bien, y el título sigue diciendo el motivo.
 *
 * No pinta nada si el paciente no está prohibido.
 *
 * Props:
 *  - paciente: la ficha (lee `prohibido`, `prohibido_motivo`,
 *    `prohibido_por_nombre`, `prohibido_at`), o bien
 *  - prohibido / motivo: sueltos (la cita de la agenda los trae así).
 *  - tamano: 'normal' | 'pequeno' | 'mini'.
 *  - interactivo: con botón y recuadro del motivo al pulsar (por defecto sí).
 */
const TAMANOS = {
  normal: 'text-xs px-2 py-0.5',
  pequeno: 'text-[10px] px-1.5 py-[1px]',
  mini: 'text-[9px] px-1 py-0 leading-[14px]',
};

const BASE = 'inline-flex shrink-0 items-center rounded font-bold uppercase tracking-wide '
  + 'bg-red-600 text-white leading-none whitespace-nowrap align-middle';

export function tituloProhibido(motivo) {
  const m = String(motivo || '').trim();
  return m ? `Paciente prohibido. Motivo (uso interno): ${m}` : 'Paciente prohibido: no se le pueden dar citas';
}

export default function IndicadorProhibido({
  paciente, prohibido, motivo, tamano = 'normal', interactivo = true,
  testId = 'indicador-prohibido', className = '',
}) {
  const marcado = prohibido === true || paciente?.prohibido === true;
  if (!marcado) return null;
  const elMotivo = motivo ?? paciente?.prohibido_motivo ?? '';
  const titulo = tituloProhibido(elMotivo);
  const clases = `${BASE} ${TAMANOS[tamano] || TAMANOS.normal} ${className}`;

  if (!interactivo) {
    return (
      <span className={clases} title={titulo} role="img" aria-label={titulo} data-testid={testId}>
        PROHIBIDO
      </span>
    );
  }

  const por = paciente?.prohibido_por_nombre;
  const cuando = paciente?.prohibido_at;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`${clases} hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400`}
          title={titulo}
          aria-label={titulo}
          data-testid={testId}
          // En una fila que se pulsa, que el clic se quede en el indicador.
          onClick={(e) => e.stopPropagation()}
        >
          PROHIBIDO
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-sm" align="start" data-testid={`${testId}-motivo`}>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-red-700">
          Paciente prohibido · motivo de uso interno
        </div>
        <p className="mt-1 whitespace-pre-wrap text-[#13231E]">
          {String(elMotivo || '').trim() || 'Sin motivo a la vista.'}
        </p>
        {(por || cuando) && (
          <div className="mt-2 text-xs text-muted-foreground">
            Marcado{por ? ` por ${por}` : ''}{cuando ? ` el ${formatDate(cuando)}` : ''}
          </div>
        )}
        <div className="mt-2 text-xs text-muted-foreground">
          No se le pueden dar citas. El motivo solo lo ve el equipo.
        </div>
      </PopoverContent>
    </Popover>
  );
}
